import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'

// 扩展InternalAxiosRequestConfig类型
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}
import { message } from 'ant-design-vue'
import { useAuthStore } from '@/stores/auth'

// 存储等待中的请求
interface PendingRequest {
  resolve: (value: any) => void
  reject: (error: any) => void
  config: InternalAxiosRequestConfig
}
const pendingRequests: PendingRequest[] = []

// 判断是否是刷新token的请求
const isRefreshTokenRequest = (config: InternalAxiosRequestConfig) => {
  return config.url?.includes('/auth/refresh-token')
}

// 获取当前语言ID
const getLanguageId = (): string => {
  const locale = localStorage.getItem('locale') || 'zh'
  const languageIdMap: Record<string, string> = {
    zh: '1',
    en: '2'
  }
  return languageIdMap[locale] || '1'
}

const request: AxiosInstance = axios.create({
  baseURL: '', // 明确设置为空字符串，避免undefined
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore()
    const token = authStore.token
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // 添加语言ID参数
    const languageId = getLanguageId()
    config.params = {
      ...config.params,
      languageId
    }
    
    // 如果是 FormData，不设置 Content-Type，让浏览器自动设置
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// 处理等待中的请求
const processPendingRequests = () => {
  pendingRequests.forEach(({ resolve, reject, config }) => {
    request(config)
      .then(resolve)
      .catch(reject)
  })
  pendingRequests.length = 0
}

// Response interceptor
request.interceptors.response.use(
  (response: AxiosResponse) => {
    // 部分 DELETE 使用 HTTP 204，无响应体；Axios 的 response.data 可能为 undefined/空串，
    // 无法解构出 code，否则会误判失败（reject 且 message 为空，控制台只显示 Error）。
    if (response.status === 204) {
      return {
        success: true,
        code: 200,
        message: 'Success',
        data: null
      }
    }

    // 文件下载等二进制响应：response.data 是 Blob / ArrayBuffer，没有 .code 字段，
    // 不能按 JSON 协议解构，直接把整个 AxiosResponse 透传给调用方处理。
    const responseType = response.config?.responseType
    if (responseType === 'blob' || responseType === 'arraybuffer') {
      return response
    }

    const { code, message: msg, data } = response.data

    if (code === 200 || code === 0) {
      return response.data
    } else {
      message.error(msg || 'Request failed')
      return Promise.reject(new Error(msg))
    }
  },
  async (error: AxiosError) => {
    const authStore = useAuthStore()
    const originalRequest = error.config as ExtendedAxiosRequestConfig
    
    // 处理401错误
    if (error.response?.status === 401) {
      // 如果是刷新token的请求返回401，直接登出并跳转
      if (isRefreshTokenRequest(originalRequest)) {
        authStore.logout()
        window.location.replace('/admin/login')
        return Promise.reject(error)
      }

      // 如果已经重试过，直接跳转登录
      if (originalRequest._retry) {
        authStore.logout()
        window.location.replace('/admin/login')
        return Promise.reject(error)
      }

      // 如果没有refresh token，直接跳转登录
      if (!authStore.refreshTokenValue) {
        authStore.logout()
        window.location.replace('/admin/login')
        return Promise.reject(error)
      }

      originalRequest._retry = true

      // 如果已经在刷新token，将请求加入队列
      if (authStore.isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({
            resolve,
            reject,
            config: originalRequest,
          })
        })
      }

      try {
        await authStore.refreshToken()
        // 刷新成功后处理队列中的请求
        processPendingRequests()
        // 重试当前请求
        return request(originalRequest)
      } catch (refreshError: any) {
        console.error('Token refresh failed:', refreshError)
        // 刷新失败，清空队列
        pendingRequests.forEach(({ reject }) => reject(refreshError))
        pendingRequests.length = 0
        // 确保登出并跳转
        authStore.logout()
        window.location.replace('/admin/login')
        return Promise.reject(refreshError)
      }
    }

    // 其他错误处理
    const errorMsg = (error.response?.data as any)?.message || error.message || 'Network error'
    message.error(errorMsg)
    return Promise.reject(error)
  }
)

export default request